import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import { createSubscriptionCheckout } from '../../services/stripeService';

// Subscription tiers - price IDs should be set in environment variables
// These are example amounts - you'll need to create products/prices in Stripe dashboard
const SUBSCRIPTION_TIERS = [
  {
    id: 'monthly_5',
    name: 'Supporter',
    amount: 3,
    description: 'Help us keep the lights on',
    priceId: process.env.REACT_APP_STRIPE_PRICE_ID_SUPPORTER || 'price_xxxxx', // Replace with actual price ID
  },
  {
    id: 'monthly_10',
    name: 'Champion',
    amount: 9,
    description: 'Support our development efforts',
    priceId: process.env.REACT_APP_STRIPE_PRICE_ID_CHAMPION || 'price_xxxxx', // Replace with actual price ID
  },
  {
    id: 'monthly_19',
    name: 'Hero',
    amount: 19,
    description: 'Make a significant impact',
    priceId: process.env.REACT_APP_STRIPE_PRICE_ID_HERO || 'price_xxxxx', // Replace with actual price ID
  },
];

export const SubscriptionDialog = ({ open, handleClose }) => {
  const [selectedTier, setSelectedTier] = useState(SUBSCRIPTION_TIERS[0].id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleTierChange = (event) => {
    setSelectedTier(event.target.value);
    setError(null);
  };

  const handleSubscribe = async () => {
    const tier = SUBSCRIPTION_TIERS.find((t) => t.id === selectedTier);
    
    if (!tier) {
      setError('Please select a subscription tier');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const successUrl = `${window.location.origin}/?subscription=success`;
      const cancelUrl = `${window.location.origin}/?subscription=cancelled`;
      
      const checkoutUrl = await createSubscriptionCheckout(tier.priceId, successUrl, cancelUrl);
      
      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err.message || 'Failed to process subscription. Please try again.');
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    if (!loading) {
      setSelectedTier(SUBSCRIPTION_TIERS[0].id);
      setError(null);
      handleClose();
    }
  };

  const selectedTierData = SUBSCRIPTION_TIERS.find((t) => t.id === selectedTier);

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div" align="center">
          Monthly Support
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          Choose a monthly subscription to support NodeCanvas
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">
              <Typography variant="subtitle1" fontWeight="bold">
                Select a tier:
              </Typography>
            </FormLabel>
            <RadioGroup
              value={selectedTier}
              onChange={handleTierChange}
              sx={{ mt: 2 }}
            >
              {SUBSCRIPTION_TIERS.map((tier) => (
                <Box
                  key={tier.id}
                  sx={{
                    border: selectedTier === tier.id ? 2 : 1,
                    borderColor: selectedTier === tier.id ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    backgroundColor: selectedTier === tier.id ? 'action.selected' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                  onClick={() => !loading && setSelectedTier(tier.id)}
                >
                  <FormControlLabel
                    value={tier.id}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="h6" component="div">
                          {tier.name} - ${tier.amount}/month
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tier.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', m: 0 }}
                  />
                </Box>
              ))}
            </RadioGroup>
          </FormControl>

          {selectedTierData && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: 'primary.light',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" align="center">
                You'll be charged <strong>${selectedTierData.amount}</strong> per month
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCloseDialog} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubscribe}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Processing...' : 'Subscribe'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

